import React, { useState, useEffect } from "react";
import {
  Box,
  Switch,
  Button,
  Typography,
  TextField,
  Card,
  CardContent,
  Grid,
  IconButton,
  MenuItem,
  FormControlLabel,
  Stack,
  Stepper,
  Step,
  StepLabel,
  Tooltip,
  CircularProgress,
} from "@mui/material";
import {
  EditOutlined,
  Delete,
  InfoOutlined,
  AddCircle,
  Save,
  Cancel,
  ArrowForward,
} from "@mui/icons-material";
import { Parser } from "hot-formula-parser";
import axios from "axios";
import { useParams, useNavigate } from "react-router-dom";
import config from "../config";
import { jwtDecode } from "jwt-decode";
import { toast } from "react-toastify";
import Pagination from "../components/Pagination";
import "react-toastify/dist/ReactToastify.css";

const baseUrl = config.baseURL;
const parser = new Parser();

// Define the primary and accent colors for consistency
const PRIMARY_COLOR = "#2E4053"; // Dark Blue
const ACCENT_COLOR = "#8B4513"; // Rich Brown

const dataTypes = [
  "text",
  "number",
  "decimal",
  "date",
  "datetime",
  "boolean",
  "dropdown",
  "file",
  "json",
  "list",
  "formula", // Added formula type for completeness if used
];

const displayTypes = [
  "dropdown",
  "range",
  "showOnlyRange",
  "file",
  "showOnlyDate",
  "showOnly",
  "list",
  "date",
  "showOnlyText",
  "textDetails",
  "icon",
  "normalTableCell",
  "input",
  "textarea",
];

const relationTypes = ["none", "belongsTo", "hasMany", "m2m"];

const formulaHelpTooltip = (
  <Box sx={{ p: 1, maxWidth: 300 }}>
    <Typography variant="body2" sx={{ fontWeight: "bold", mb: 1 }}>
      Supported Excel-like Formulas:
    </Typography>
    <Typography variant="body2">
      • <b>SUM(a,b,...)</b> – Add numbers
      <br />• <b>AVERAGE(a,b,...)</b> – Mean value
      <br />• <b>MIN(a,b,...)</b> / <b>MAX(a,b,...)</b> – Lowest or highest
      <br />• <b>IF(condition, true, false)</b> – Conditional
      <br />• <b>CONCAT(text1, text2, ...)</b> – Join text
      <br />• <b>ROUND(number, digits)</b> – Round number
      <br />• <b>TODAY()</b> / <b>NOW()</b> – Current date/time
      <br />• <b>LEN(text)</b>, <b>UPPER(text)</b>, <b>LOWER(text)</b>
      <br />• <b>ABS(number)</b> – Absolute value
      <br />• <b>DATEDIFF(date1, date2)</b> – Days difference
      <br />
      <br />
      <Typography variant="caption" color="textSecondary">
        (Note: Field accessors can be used as formula arguments, e.g.,
        `SUM(price, tax)`)
      </Typography>
    </Typography>
  </Box>
);

// Custom styles for TextFields
const customTextFieldStyle = {
  "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
    borderColor: PRIMARY_COLOR,
  },
  "& .MuiInputLabel-root.Mui-focused": {
    color: PRIMARY_COLOR,
  },
};

const ModuleFields = () => {
  const { id } = useParams();
  const [token] = useState(localStorage.getItem("token"));
  const [modulesList, setModulesList] = useState([]);
  const [loadingModules, setLoadingModules] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemPerPage, setItemPerPage] = useState(10);
  const [totalItems, setTotalItems] = useState(0);
  const [expandedField, setExpandedField] = useState(null);

  const [relatedFields, setRelatedFields] = useState({});
  const [fields, setFields] = useState([]);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  // Pre-defined built-in modules
  const builtInModules = [
    { id: -1, name: "Customers", type: "builtin", table: "customers" },
    { id: -2, name: "Categories", type: "builtin", table: "categories" },
    { id: -3, name: "Complaints", type: "builtin", table: "complaint" },
    { id: -4, name: "Earnings", type: "builtin", table: "earnings" },
    { id: -5, name: "Expenses", type: "builtin", table: "expenses" },
    { id: -6, name: "Invoices", type: "builtin", table: "invoice" },
    { id: -7, name: "Materials", type: "builtin", table: "materials" },
    { id: -8, name: "Orders", type: "builtin", table: "orders" },
    {
      id: -9,
      name: "Production Batch",
      type: "builtin",
      table: "ProductionBatch",
    },
    { id: -10, name: "Products", type: "builtin", table: "products" },
    { id: -11, name: "Reward Points", type: "builtin", table: "rewardPoints" },
    {
      id: -12,
      name: "Shipping Company",
      type: "builtin",
      table: "ShippingCompany",
    },
    { id: -13, name: "Suppliers", type: "builtin", table: "suppliers" },
    { id: -14, name: "Vendors", type: "builtin", table: "vendors" },
  ];

  const fetchModules = async () => {
    try {
      const res = await axios.get(`${baseUrl}/api/module`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const dynamicModules = res.data?.data || res.data || [];
      const combined = [...builtInModules, ...dynamicModules];
      setModulesList(combined);
    } catch (err) {
      console.error("Error loading modules list", err);
      setModulesList(builtInModules);
    } finally {
      setLoadingModules(false);
    }
  };

  useEffect(() => {
    fetchModules();
  }, [token]);

  const handleFetchFields = async (moduleId, idx) => {
    if (!moduleId) return;
    try {
      const res = await axios.get(`${baseUrl}/api/module/${moduleId}/fields`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const data = res.data?.data || [];
      setRelatedFields((prev) => ({
        ...prev,
        [idx]: data, // store fields for this module index
      }));
    } catch (err) {
      console.error("Error fetching related module fields", err);
      setRelatedFields((prev) => ({
        ...prev,
        [idx]: [],
      }));
    }
  };

  const handleFetchBuiltInFields = async (tableName, idx) => {
    try {
      const res = await axios.get(
        `${baseUrl}/api/module/builtin/${tableName}/fields`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      const fetchedFields = Array.isArray(res.data)
        ? res.data
        : Array.isArray(res.data?.data)
        ? res.data.data
        : [];

      setRelatedFields((prev) => ({
        ...prev,
        [idx]: fetchedFields,
      }));
    } catch (err) {
      console.error("❌ Error fetching builtin module fields", err);
      setRelatedFields((prev) => ({
        ...prev,
        [idx]: [],
      }));
    }
  };

  const handleModuleSelect = async (moduleId, idx) => {
    const selectedModule = modulesList.find((m) => m.id === moduleId);

    if (!selectedModule) {
      // Clear fields if no module selected
      setFields((prev) =>
        prev.map((f, i) =>
          i === idx
            ? { ...f, relation_module_id: null, relation_field_accessor: "" }
            : f
        )
      );
      setRelatedFields((prev) => ({ ...prev, [idx]: [] }));
      return;
    }

    // Reset relation_field_accessor while loading
    setFields((prev) =>
      prev.map((f, i) =>
        i === idx
          ? { ...f, relation_module_id: moduleId, relation_field_accessor: "" }
          : f
      )
    );

    let fetchedFields = [];

    try {
      if (selectedModule.type === "builtin") {
        const res = await axios.get(
          `${baseUrl}/api/module/builtin/${selectedModule.table}/fields`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        fetchedFields = Array.isArray(res.data?.data)
          ? res.data.data
          : Array.isArray(res.data)
          ? res.data
          : [];
      } else {
        const res = await axios.get(
          `${baseUrl}/api/module/${moduleId}/fields`,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );
        fetchedFields = res.data?.data || [];
      }

      setRelatedFields((prev) => ({ ...prev, [idx]: fetchedFields }));

      // Optionally auto-select the first field if exists
      if (fetchedFields.length > 0) {
        setFields((prev) =>
          prev.map((f, i) =>
            i === idx
              ? {
                  ...f,
                  relation_field_accessor: fetchedFields[0].accessor || "",
                }
              : f
          )
        );
      }
    } catch (err) {
      console.error("Error fetching module fields:", err);
      setRelatedFields((prev) => ({ ...prev, [idx]: [] }));
    }
  };

  useEffect(() => {
    if (id && modulesList.length > 0) {
      fetchExistingFields(1);
    }
  }, [id, modulesList]);

  const handleRemoveField = async (idx) => {
    const field = fields[idx];
    if (!field?.id) {
      // If it's a new unsaved field, just remove it from the list
      setFields((prev) => prev.filter((_, i) => i !== idx));
      if (expandedField === idx) setExpandedField(null);
      return;
    }

    if (
      window.confirm(
        "Are you sure you want to delete this field? This action cannot be undone."
      )
    ) {
      try {
        const res = await axios.delete(
          `${baseUrl}/api/module-field/${field.id}`,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );

        if (res.status === 200) {
          toast.success("Field deleted successfully");
          setFields((prev) => prev.filter((_, i) => i !== idx));
          if (expandedField === idx) setExpandedField(null);
        }
      } catch (err) {
        console.error("Delete failed:", err);
        toast.error("Failed to delete field");
      }
    }
  };

  const fetchExistingFields = async (page = 1) => {
    setLoading(true);
    try {
      const res = await axios.get(
        `${baseUrl}/api/module/${id}/fields?page=${page}&itemsPerPage=${itemPerPage}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      const fetchedFields = Array.isArray(res.data?.data)
        ? res.data.data
        : Array.isArray(res.data)
        ? res.data
        : [];

      setTotalItems(res.data?.pagination?.totalItems || fetchedFields.length);
      setCurrentPage(page);

      if (fetchedFields.length > 0) {
        const initialFields = fetchedFields.map((f) => ({
          ...f,
          isEditing: false,
          isNew: false,
        }));
        setFields(initialFields);

        // Prefetch related fields for all relations (only for current page)
        initialFields.forEach((f, idx) => {
          if (f.relation_module_id) {
            const selectedModule = modulesList.find(
              (m) => m.id === f.relation_module_id
            );
            if (selectedModule) {
              if (selectedModule.type === "builtin") {
                handleFetchBuiltInFields(selectedModule.table, idx);
              } else {
                handleFetchFields(f.relation_module_id, idx);
              }
            }
          }
        });
      } else if (page === 1) {
        // Only show one empty field if no fields exist on the first page
        setFields([
          {
            accessor: "",
            label: "",
            data_type: "text",
            display_type: "input",
            required: false,
            sortable: true,
            filterable: true,
            readonly: false,
            relation_type: "none",
            relation_module_id: null,
            relation_field_accessor: "",
            hasFormula: false,
            formula: "",
            order_index: 0,
            isEditing: true,
            isNew: true,
          },
        ]);
        setExpandedField(0);
      } else {
        setFields([]);
      }
    } catch (err) {
      console.error("Error loading module fields", err);
      toast.error("Failed to load module fields");
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (page) => {
    fetchExistingFields(page);
  };

  const handleAddField = () => {
    const newField = {
      accessor: "",
      label: "",
      data_type: "text",
      display_type: "input",
      required: false,
      sortable: true,
      filterable: true,
      readonly: false,
      relation_type: "none",
      relation_module_id: null,
      relation_field_accessor: "",
      hasFormula: false,
      formula: "",
      order_index:
        fields.length > 0
          ? Math.max(...fields.map((f) => f.order_index)) + 1
          : 0,
      isEditing: true,
      isNew: true,
    };

    setFields((prev) => [newField, ...prev]); // Add new field to the top
    setExpandedField(0); // Auto expand the new one which is now at index 0
  };

  const handleSaveSingleField = async (field, idx) => {
    setLoading(true);

    // --- Pre-save validation ---
    if (!field.label || !field.accessor) {
      toast.error("Field Label and Accessor cannot be empty.");
      setLoading(false);
      return;
    }

    if (field.hasFormula && field.formula) {
      const error = validateFormula(field.formula);
      if (error) {
        toast.error(`Formula Error: ${error}`);
        setLoading(false);
        return;
      }
    }
    // --- End Validation ---

    try {
      const config = {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      };
      const payload = {
        module_id: id,
        label: field.label,
        accessor: field.accessor,
        data_type: field.data_type,
        display_type: field.display_type,
        required: field.required,
        sortable: field.sortable,
        filterable: field.filterable,
        readonly: field.readonly,
        order_index: field.order_index,

        relation_type: field.relation_type,
        relation_module_id:
          field.relation_type === "none" ? null : field.relation_module_id,
        relation_field_accessor:
          field.relation_type === "none" ? null : field.relation_field_accessor,

        hasFormula: field.hasFormula,
        formula: field.hasFormula ? field.formula : null,
      };

      // const payload = {
      //   module_id: id,
      //   ...field,
      //   // Clean up transient state properties
      //   isEditing: undefined,
      //   isNew: undefined,
      // };

      let res;
      let action;

      if (!field.id || field.isNew) {
        // 🆕 CREATE new field
        res = await axios.post(
          `${baseUrl}/api/module-field`,
          { module_id: id, fields: [payload] },
          config
        );
        action = "created";
      } else {
        // ✏️ UPDATE existing field
        res = await axios.put(
          `${baseUrl}/api/module-field/${field.id}`,
          payload,
          config
        );
        action = "updated";
      }

      toast.success(`Field successfully ${action}!`);

      // 🎯 Automatically refresh list and close edit mode
      fetchExistingFields(currentPage);
      setExpandedField(null);
    } catch (err) {
      console.error("❌ Save failed:", err.response?.data || err);
      toast.error(err.response?.data?.message || "Error saving field(s)");
    } finally {
      setLoading(false);
    }
  };

  // --- Formula Validation ---
  const validateFormula = (formula) => {
    if (!formula.trim()) return null;
    try {
      // The parser expects variables to be defined, but we are just checking syntax here.
      // We wrap the formula in a simple operation to force evaluation of functions.
      const result = parser.parse(`IF(TRUE, ${formula}, 0)`);
      if (result.error && result.error !== "#NAME?") {
        // Ignore #NAME? error which is expected for undefined custom fields
        return result.error;
      }
      return null;
    } catch (e) {
      return e.message;
    }
  };

  const handleFieldChange = (idx, key, value) => {
    setFields((prev) =>
      prev.map((f, i) =>
        i === idx
          ? {
              ...f,
              [key]: value,
              // Reset relation fields if relation_type is "none"
              ...(key === "relation_type" && value === "none"
                ? {
                    relation_module_id: null,
                    relation_field_accessor: "",
                  }
                : {}),
            }
          : f
      )
    );
  };

  return (
    <Box
      sx={{
        p: { xs: 2, sm: 4 },
        minHeight: "100vh",
        backgroundColor: "#F4F6F8",
        fontFamily: "Poppins, sans-serif",
      }}
    >
      <Typography
        variant="h4"
        sx={{
          fontWeight: "600",
          mb: 4,
          color: PRIMARY_COLOR,
        }}
      >
        🛠️ Module Fields Configuration
      </Typography>

      {/* Stepper Styling */}
      <Stepper
        activeStep={1}
        sx={{
          mb: 5,
          "& .MuiStepIcon-root.Mui-active": { color: PRIMARY_COLOR },
          "& .MuiStepIcon-root.Mui-completed": { color: PRIMARY_COLOR },
          "& .MuiStepLabel-label": { fontWeight: 500, color: "#34495E" },
        }}
      >
        {["Module Info", "Add Fields", "Permissions"].map((label) => (
          <Step key={label}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>

      {/* Add Field Button */}
      <Button
        startIcon={<AddCircle />}
        onClick={handleAddField}
        variant="contained"
        sx={{
          mb: 3,
          backgroundColor: PRIMARY_COLOR,
          color: "#FFFFFF",
          "&:hover": { backgroundColor: "#1C2A3D" },
          textTransform: "none",
          fontWeight: 600,
        }}
      >
        Add New Field
      </Button>

      {/* Field Cards */}
      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", p: 5 }}>
          <CircularProgress />
        </Box>
      ) : fields.length === 0 && totalItems === 0 ? (
        <Typography
          variant="body1"
          color="textSecondary"
          sx={{ mt: 3, p: 2, border: "1px dashed #AAB7B8", borderRadius: 2 }}
        >
          No fields found for this module. Click 'Add New Field' to begin
          configuration.
        </Typography>
      ) : (
        fields.map((field, idx) => (
          <Card
            key={field.id || `new-${idx}`}
            sx={{
              mb: 2,
              border: field.isNew
                ? `2px dashed ${ACCENT_COLOR}`
                : `1px solid #E0E6ED`,
              backgroundColor: field.isNew
                ? "#FFF8F2" // Very light accent background
                : field.isEditing
                ? "#F8FAFC"
                : "#FFFFFF",
              borderRadius: 3,
              boxShadow: field.isNew
                ? `0 0 12px rgba(139, 69, 19, 0.4)` // Brown glow
                : "0 3px 8px rgba(0,0,0,0.04), 0 1px 3px rgba(0,0,0,0.02)",
              transition: "all 0.25s ease-in-out",
              overflow: "hidden",
            }}
          >
            {/* CARD HEADER */}
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                backgroundColor: field.isNew ? "#FBEFE0" : PRIMARY_COLOR, // Accent color background for new
                color: field.isNew ? PRIMARY_COLOR : "#FFFFFF",
                borderBottom: "1px solid #E0E6ED",
                px: 3,
                py: 1.5,
                cursor: "pointer",
                "&:hover": {
                  backgroundColor: field.isNew ? "#FBE5D5" : "#1C2A3D",
                },
              }}
              onClick={() =>
                setExpandedField(expandedField === idx ? null : idx)
              }
            >
              <Typography sx={{ fontWeight: 600 }}>
                {field.label ||
                  (field.isNew ? "✨ New Field (Unsaved)" : `Field ${idx + 1}`)}
              </Typography>
              <Typography sx={{ fontSize: "0.85rem", fontWeight: 500 }}>
                {expandedField === idx ? "▲ Collapse" : "▼ Expand"}
              </Typography>
            </Box>

            {/* COLLAPSIBLE CONTENT */}
            <Box
              sx={{
                maxHeight: expandedField === idx ? "2000px" : "0px",
                overflow: "hidden",
                transition: "max-height 0.4s ease",
              }}
            >
              <CardContent sx={{ p: 3 }}>
                {!field.isEditing ? (
                  <Box sx={{ position: "relative", pr: 6 }}>
                    {/* VIEW MODE */}
                    <Grid container spacing={2}>
                      {/* LEFT SIDE */}
                      <Grid item xs={12} md={6}>
                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                            gap: 1.5,
                          }}
                        >
                          <Typography
                            sx={{ color: "#5D6D7E", fontWeight: 500 }}
                          >
                            Label:
                          </Typography>
                          <Typography
                            sx={{ fontWeight: 600, color: PRIMARY_COLOR }}
                          >
                            {field.label || "—"}
                          </Typography>

                          <Typography
                            sx={{ color: "#5D6D7E", fontWeight: 500 }}
                          >
                            Accessor:
                          </Typography>
                          <Typography
                            sx={{ fontFamily: "monospace", color: "#7F8C8D" }}
                          >
                            {field.accessor || "—"}
                          </Typography>

                          <Typography
                            sx={{ color: "#5D6D7E", fontWeight: 500 }}
                          >
                            Data Type / Display:
                          </Typography>
                          <Typography>
                            {field.data_type || "—"} /{" "}
                            {field.display_type || "—"}
                          </Typography>

                          <Typography
                            sx={{ color: "#5D6D7E", fontWeight: 500 }}
                          >
                            Formula:
                          </Typography>
                          <Typography
                            sx={{
                              color: field.hasFormula
                                ? PRIMARY_COLOR
                                : "#7F8C8D",
                              fontFamily: "monospace",
                            }}
                          >
                            {field.hasFormula
                              ? field.formula || "(Enabled, but empty)"
                              : "Disabled"}
                          </Typography>
                        </Box>
                      </Grid>

                      {/* RIGHT SIDE */}
                      <Grid item xs={12} md={6}>
                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                            gap: 1.5,
                          }}
                        >
                          <Typography
                            sx={{ color: "#5D6D7E", fontWeight: 500 }}
                          >
                            Relation Type:
                          </Typography>
                          <Typography>
                            {field.relation_type || "none"}
                          </Typography>

                          {field.relation_module_id && (
                            <>
                              <Typography
                                sx={{ color: "#5D6D7E", fontWeight: 500 }}
                              >
                                Relation Module:
                              </Typography>
                              <Typography>
                                {modulesList.find(
                                  (m) => m.id === field.relation_module_id
                                )?.name || field.relation_module_id}
                              </Typography>
                            </>
                          )}

                          {field.relation_field_accessor && (
                            <>
                              <Typography
                                sx={{ color: "#5D6D7E", fontWeight: 500 }}
                              >
                                Relation Field:
                              </Typography>
                              <Typography>
                                {field.relation_field_accessor || "—"}
                              </Typography>
                            </>
                          )}

                          <Typography
                            sx={{ color: "#5D6D7E", fontWeight: 500 }}
                          >
                            Flags:
                          </Typography>
                          <Stack direction="row" spacing={1} flexWrap="wrap">
                            {field.required && (
                              <Box
                                component="span"
                                sx={{
                                  px: 1,
                                  py: 0.5,
                                  backgroundColor: "#FADBD8",
                                  color: "#C0392B",
                                  borderRadius: 1,
                                  fontSize: "0.75rem",
                                }}
                              >
                                Required
                              </Box>
                            )}
                            {field.readonly && (
                              <Box
                                component="span"
                                sx={{
                                  px: 1,
                                  py: 0.5,
                                  backgroundColor: "#EBEDEF",
                                  color: "#5D6D7E",
                                  borderRadius: 1,
                                  fontSize: "0.75rem",
                                }}
                              >
                                Readonly
                              </Box>
                            )}
                            {field.sortable && (
                              <Box
                                component="span"
                                sx={{
                                  px: 1,
                                  py: 0.5,
                                  backgroundColor: "#E8F6F3",
                                  color: "#1ABC9C",
                                  borderRadius: 1,
                                  fontSize: "0.75rem",
                                }}
                              >
                                Sortable
                              </Box>
                            )}
                            {field.filterable && (
                              <Box
                                component="span"
                                sx={{
                                  px: 1,
                                  py: 0.5,
                                  backgroundColor: "#EBF5FB",
                                  color: "#3498DB",
                                  borderRadius: 1,
                                  fontSize: "0.75rem",
                                }}
                              >
                                Filterable
                              </Box>
                            )}
                          </Stack>
                        </Box>
                      </Grid>
                    </Grid>

                    {/* ACTIONS (Edit/Delete) */}
                    <Tooltip title="Edit Field">
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          setFields((prev) =>
                            prev.map((f, i) =>
                              i === idx ? { ...f, isEditing: true } : f
                            )
                          );
                          setExpandedField(idx);
                        }}
                        sx={{
                          position: "absolute",
                          top: 8,
                          right: 40,
                          backgroundColor: "#EBF5FB",
                          color: PRIMARY_COLOR,
                          "&:hover": { backgroundColor: "#D6EAF8" },
                        }}
                      >
                        <EditOutlined fontSize="small" />
                      </IconButton>
                    </Tooltip>

                    <Tooltip
                      title={field.id ? "Delete Field" : "Remove New Field"}
                    >
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleRemoveField(idx);
                        }}
                        sx={{
                          position: "absolute",
                          top: 8,
                          right: 8,
                          color: "#E74C3C",
                          "&:hover": { backgroundColor: "#FDEDEC" },
                        }}
                      >
                        <Delete fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                ) : (
                  <>
                    {/* EDIT MODE */}
                    <Grid container spacing={2.5}>
                      {/* Row 1: Label, Accessor, Types */}
                      <Grid item xs={12} sm={4}>
                        <TextField
                          fullWidth
                          label="Label"
                          variant="outlined"
                          value={field.label}
                          onChange={(e) => {
                            const label = e.target.value;
                            const accessor = label
                              .toLowerCase()
                              .trim()
                              .replace(/\s+/g, "_")
                              .replace(/[^a-z0-9_]/g, "");
                            setFields((prev) =>
                              prev.map((f, i) =>
                                i === idx ? { ...f, label, accessor } : f
                              )
                            );
                          }}
                          sx={customTextFieldStyle}
                          error={!field.label}
                          helperText={!field.label && "Required"}
                        />
                        <TextField
                          fullWidth
                          label="Accessor (Auto-Generated)"
                          variant="outlined"
                          value={field.accessor}
                          disabled
                          sx={{ mt: 1, ...customTextFieldStyle }}
                        />
                      </Grid>

                      <Grid item xs={12} sm={4}>
                        <TextField
                          select
                          label="Data Type"
                          fullWidth
                          variant="outlined"
                          value={field.data_type}
                          onChange={(e) =>
                            handleFieldChange(idx, "data_type", e.target.value)
                          }
                          sx={customTextFieldStyle}
                        >
                          {dataTypes.map((type) => (
                            <MenuItem key={type} value={type}>
                              {type}
                            </MenuItem>
                          ))}
                        </TextField>
                        <TextField
                          select
                          label="Display Type"
                          fullWidth
                          variant="outlined"
                          value={field.display_type}
                          onChange={(e) =>
                            handleFieldChange(
                              idx,
                              "display_type",
                              e.target.value
                            )
                          }
                          sx={{ mt: 2, ...customTextFieldStyle }}
                        >
                          {displayTypes.map((type) => (
                            <MenuItem key={type} value={type}>
                              {type}
                            </MenuItem>
                          ))}
                        </TextField>
                      </Grid>

                      {/* Order Index & Formula */}
                      <Grid item xs={12} sm={4}>
                        <TextField
                          label="Order Index"
                          type="number"
                          fullWidth
                          value={field.order_index}
                          onChange={(e) =>
                            handleFieldChange(
                              idx,
                              "order_index",
                              parseInt(e.target.value) || 0
                            )
                          }
                          sx={customTextFieldStyle}
                          InputLabelProps={{ shrink: true }}
                        />

                        {/* Tooltip for "Coming Soon" */}
                        <Tooltip title="Coming Soon" arrow>
                          <div>
                            <FormControlLabel
                              control={
                                <Switch
                                  checked={false} // Always off
                                  disabled // Non-interactive
                                  sx={{
                                    "& .MuiSwitch-switchBase.Mui-checked": {
                                      color: ACCENT_COLOR,
                                    },
                                    "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track":
                                      {
                                        backgroundColor: ACCENT_COLOR,
                                      },
                                  }}
                                />
                              }
                              label={
                                <Typography
                                  sx={{ fontWeight: 500, color: PRIMARY_COLOR }}
                                >
                                  Enable Formula
                                </Typography>
                              }
                              sx={{ mt: 2, ml: 0 }}
                            />
                          </div>
                        </Tooltip>
                      </Grid>

                      {/* Row 2: Formula Input */}
                      {field.hasFormula && (
                        <Grid item xs={12}>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "flex-start",
                              gap: 1,
                            }}
                          >
                            <TextField
                              fullWidth
                              label="Formula Expression (e.g., SUM(field_a, field_b))"
                              variant="outlined"
                              value={field.formula}
                              onChange={(e) =>
                                handleFieldChange(
                                  idx,
                                  "formula",
                                  e.target.value
                                )
                              }
                              sx={{ flexGrow: 1, ...customTextFieldStyle }}
                              error={!!validateFormula(field.formula)}
                              helperText={
                                validateFormula(field.formula) ||
                                "Use field accessors for calculations"
                              }
                            />
                            <Tooltip title={formulaHelpTooltip} arrow>
                              <IconButton
                                color="info"
                                sx={{ mt: 1, color: PRIMARY_COLOR }}
                              >
                                <InfoOutlined />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </Grid>
                      )}

                      {/* Row 3: Relations */}
                      <Grid item xs={12}>
                        <Typography
                          variant="h6"
                          sx={{
                            mt: 2,
                            mb: 1,
                            fontWeight: 600,
                            color: PRIMARY_COLOR,
                          }}
                        >
                          Relationships
                        </Typography>
                        <Grid container spacing={2.5}>
                          <Grid item xs={12} sm={4}>
                            <TextField
                              select
                              label="Relation Type"
                              fullWidth
                              variant="outlined"
                              value={field.relation_type}
                              onChange={(e) =>
                                handleFieldChange(
                                  idx,
                                  "relation_type",
                                  e.target.value
                                )
                              }
                              sx={customTextFieldStyle}
                            >
                              {relationTypes.map((type) => (
                                <MenuItem key={type} value={type}>
                                  {type}
                                </MenuItem>
                              ))}
                            </TextField>
                          </Grid>

                          <Grid item xs={12} sm={4}>
                            <TextField
                              select
                              label="Relation Module"
                              fullWidth
                              variant="outlined"
                              value={field.relation_module_id || ""}
                              onChange={(e) =>
                                handleModuleSelect(e.target.value, idx)
                              }
                              disabled={field.relation_type === "none"}
                              sx={customTextFieldStyle}
                            >
                              {modulesList.map((module) => (
                                <MenuItem key={module.id} value={module.id}>
                                  {module.name}
                                </MenuItem>
                              ))}
                            </TextField>
                          </Grid>

                          <Grid item xs={12} sm={4}>
                            <TextField
                              select
                              label="Relation Field"
                              fullWidth
                              variant="outlined"
                              value={field.relation_field_accessor || ""}
                              onChange={(e) =>
                                handleFieldChange(
                                  idx,
                                  "relation_field_accessor",
                                  e.target.value
                                )
                              }
                              disabled={
                                field.relation_type === "none" ||
                                !field.relation_module_id
                              }
                              sx={customTextFieldStyle}
                            >
                              {(relatedFields[idx] || []).map((rf) => (
                                <MenuItem key={rf.accessor} value={rf.accessor}>
                                  {rf.label || rf.accessor}
                                </MenuItem>
                              ))}
                            </TextField>
                          </Grid>
                        </Grid>
                      </Grid>

                      {/* Row 4: Toggles */}
                      {/* <Grid item xs={12}>
                        <Typography
                          variant="h6"
                          sx={{
                            mt: 2,
                            mb: 1,
                            fontWeight: 600,
                            color: PRIMARY_COLOR,
                          }}
                        >
                          Field Flags
                        </Typography>
                        <Grid container spacing={2}>
                          {[
                            "required",
                            "sortable",
                            "filterable",
                            "readonly",
                          ].map((key) => (
                            <Grid item key={key} xs={6} sm={3}>
                              <FormControlLabel
                                control={
                                  <Switch
                                    checked={field[key]}
                                    onChange={(e) =>
                                      handleFieldChange(
                                        idx,
                                        key,
                                        e.target.checked
                                      )
                                    }
                                    sx={{
                                      "& .MuiSwitch-switchBase.Mui-checked": {
                                        color: ACCENT_COLOR,
                                      },
                                      "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track":
                                        { backgroundColor: ACCENT_COLOR },
                                    }}
                                  />
                                }
                                label={
                                  <Typography
                                    sx={{
                                      fontWeight: 500,
                                      color: PRIMARY_COLOR,
                                    }}
                                  >
                                    {key.charAt(0).toUpperCase() + key.slice(1)}
                                  </Typography>
                                }
                              />
                            </Grid>
                          ))}
                        </Grid>
                      </Grid> */}
                    </Grid>

                    {/* Save / Cancel */}
                    <Stack
                      direction="row"
                      spacing={2}
                      sx={{ mt: 4, justifyContent: "flex-end" }}
                    >
                      <Button
                        variant="contained"
                        startIcon={
                          loading ? (
                            <CircularProgress size={20} color="inherit" />
                          ) : (
                            <Save />
                          )
                        }
                        onClick={() => handleSaveSingleField(field, idx)}
                        disabled={loading || !field.label || !field.accessor}
                        sx={{
                          textTransform: "none",
                          fontWeight: 600,
                          px: 3,
                          backgroundColor: ACCENT_COLOR,
                          "&:hover": { backgroundColor: "#65310B" },
                        }}
                      >
                        {field.id ? "Update Field" : "Save Field"}
                      </Button>
                      <Button
                        variant="outlined"
                        startIcon={<Cancel />}
                        color="secondary"
                        onClick={() => {
                          if (field.isNew) {
                            handleRemoveField(idx); // Use delete handler for unsaved fields
                          } else {
                            // Cancel edit mode
                            setFields((prev) =>
                              prev.map((f, i) =>
                                i === idx ? { ...f, isEditing: false } : f
                              )
                            );
                            setExpandedField(null);
                          }
                        }}
                        sx={{
                          textTransform: "none",
                          fontWeight: 600,
                          px: 3,
                          color: PRIMARY_COLOR,
                          borderColor: PRIMARY_COLOR,
                        }}
                      >
                        Cancel
                      </Button>
                    </Stack>
                  </>
                )}
              </CardContent>
            </Box>
          </Card>
        ))
      )}

      {/* Navigation Buttons */}
      <Stack
        direction="row"
        spacing={2}
        sx={{ mt: 4, pt: 3, borderTop: "1px solid #E0E6ED" }}
      >
        <Button
          variant="contained"
          endIcon={<ArrowForward />}
          onClick={() => navigate(`/dashboard/modules/permission/${id}`)}
          sx={{
            backgroundColor: PRIMARY_COLOR,
            "&:hover": { backgroundColor: "#1C2A3D" },
            textTransform: "none",
            fontWeight: 600,
            px: 4,
          }}
        >
          Continue to Permissions
        </Button>
      </Stack>

      {/* Pagination */}
      <Box sx={{ mt: 5 }}>
        {totalItems > 0 && (
          <Pagination
            totalItem={totalItems}
            itemPerPage={itemPerPage}
            currentPage={currentPage}
            onPageChange={handlePageChange}
            fetchItems={fetchExistingFields}
          />
        )}
      </Box>
    </Box>
  );
};

export default ModuleFields;
